"use client";

import { CTA_SOURCES, PAGE_SECTIONS } from "@punchup/analytics";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ShowInfo, ShowWithComedian } from "@/data/shows";

import { Comedian } from "@/data/comedians";
import { ShowRowContextValue } from "@/components/shows/types";
import { WaitlistSubscription } from "@punchup/data/client";
import { getCDNImageURL } from "@/data/images";
import { getStoredShortLinkContext } from "@/components/providers/ShortLinkProvider";
import { isTixologiCheckoutShow } from "@punchup/tixologi/widget-origin";
import { useGate } from "statsig-react";
import { useShowRowAnalytics } from "@/hooks/shows/useShowRowAnalytics";
import { useShowRowBadges } from "@/hooks/shows/useShowRowBadges";
import { useShowRowComedians } from "@/hooks/shows/useShowRowComedians";

interface ShowRowProviderProps {
  shows: ShowInfo[];
  comedian: Comedian;
  showcaseShow?: ShowWithComedian;
  location: string;
  isNearby?: boolean;
  internalReferrer?: string;
  buyTicketsShowId?: string;
  setBuyTicketsShowId?: (id: string) => void;
  isFollowed?: boolean;
  venueSlug?: string | null;
  followedComedianIds?: string[];
  hideComedianAvatar?: boolean;
  hideHeatingUp?: boolean;
  hideUsersChecking?: boolean;
  hideComedianName?: boolean;
  pageSection?: PAGE_SECTIONS;
  ctaSource?: CTA_SOURCES;
  waitlistSubscription?: WaitlistSubscription | null;
  children: ReactNode;
}

const ShowRowContext = createContext<ShowRowContextValue | null>(null);

export function ShowRowProvider({
  shows,
  comedian,
  showcaseShow,
  location,
  isNearby = false,
  internalReferrer,
  buyTicketsShowId,
  setBuyTicketsShowId,
  isFollowed = false,
  venueSlug,
  followedComedianIds = [],
  hideComedianAvatar = false,
  hideHeatingUp = false,
  hideUsersChecking = false,
  hideComedianName = false,
  pageSection,
  ctaSource,
  waitlistSubscription,
  children,
}: ShowRowProviderProps) {
  const { value: ticketmasterAffiliateEnabled } = useGate(
    "ticketmaster_affiliate_converter"
  );
  const shortLinkCtx = getStoredShortLinkContext();
  const { shortCode } = shortLinkCtx || {};
  const [isComediansDrawerOpen, setIsComediansDrawerOpen] = useState(false);

  const multipleShows = shows.length > 1;
  const firstShow = shows[0];

  // Check if this is a Ticketmaster event
  const isTicketmasterEvent = firstShow.id.startsWith("tm-");

  // TODO: don't hardcode to first show?
  const isTixologi = isTixologiCheckoutShow(
    firstShow.ticketLink,
    firstShow.tixologiEventId
  );

  // Extract hooks
  const { showcaseComedians } = useShowRowComedians(
    showcaseShow,
    followedComedianIds
  );

  const { usersChecking, isHeatingUp, badgesLoading } = useShowRowBadges(
    shows,
    {
      enabled: !(hideHeatingUp && hideUsersChecking),
    }
  );

  const {
    handleComedianNameClick,
    trackBuyTickets,
    handleBuyTicketsClick,
    handleShare,
  } = useShowRowAnalytics({
    show: firstShow,
    comedian,
    location,
    isNearby,
    internalReferrer,
    shortCode,
    isTicketmasterEvent,
    isTixologi,
    ticketmasterAffiliateEnabled,
    buyTicketsShowId,
    setBuyTicketsShowId,
    pageSection,
    ctaSource,
  });

  // Get unique venues
  const uniqueVenues = useMemo(() => {
    return Array.from(new Set(shows.map((show) => show.venue)));
  }, [shows]);

  const earliestShowDate = useMemo(
    () =>
      multipleShows
        ? shows.reduce((min, show) => {
            return show.datetime.isBefore(min) ? show.datetime : min;
          }, shows[0].datetime)
        : firstShow.datetime,
    [shows, multipleShows, firstShow.datetime]
  );

  const latestShowDate = useMemo(
    () =>
      multipleShows
        ? shows.reduce((max, show) => {
            return show.datetime.isAfter(max) ? show.datetime : max;
          }, shows[0].datetime)
        : firstShow.datetime,
    [shows, multipleShows, firstShow.datetime]
  );

  const someShowHasLowTickets = useMemo(
    () => shows.some((show) => show.flags.includes("LOW_TICKETS")),
    [shows]
  );

  const hasBadges = useMemo(() => {
    return (
      (!hideHeatingUp && isHeatingUp) ||
      (!hideUsersChecking && usersChecking > 0) ||
      someShowHasLowTickets
    );
  }, [
    hideHeatingUp,
    isHeatingUp,
    hideUsersChecking,
    usersChecking,
    someShowHasLowTickets,
  ]);

  const hasPresaleCode = useMemo(() => {
    return !!firstShow.presaleCode && firstShow.presaleCode.length > 0;
  }, [firstShow.presaleCode]);

  // Helper function to get image URL based on preference
  const getImageUrl = useCallback(
    (comedianData: {
      ticketmaster_event_image?: string;
      about_img?: string | null;
    }): string | undefined => {
      // For Ticketmaster shows, prefer the event image if available
      if (comedianData?.ticketmaster_event_image) {
        return comedianData.ticketmaster_event_image;
      }

      // Default to comedian avatar (existing behavior)
      return comedianData?.about_img
        ? getCDNImageURL(comedianData.about_img)
        : undefined;
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      shows,
      earliestShowDate,
      latestShowDate,
      uniqueVenues,
      comedian,
      showcaseComedians,
      isFollowed,
      followedComedianIds,
      isTicketmasterEvent,
      handleComedianNameClick,
      trackBuyTickets,
      handleBuyTicketsClick,
      handleShare,
      getImageUrl,
      usersChecking,
      isHeatingUp,
      badgesLoading,
      hideComedianAvatar,
      hideComedianName,
      hideHeatingUp,
      hideUsersChecking,
      hasBadges,
      hasPresaleCode,
      isTixologi,
      venueSlug,
      location,
      isNearby,
      internalReferrer,
      isComediansDrawerOpen,
      setIsComediansDrawerOpen,
      pageSection,
      ctaSource,
      waitlistSubscription,
    }),
    [
      shows,
      earliestShowDate,
      latestShowDate,
      uniqueVenues,
      comedian,
      showcaseComedians,
      isFollowed,
      followedComedianIds,
      isTicketmasterEvent,
      handleComedianNameClick,
      trackBuyTickets,
      handleBuyTicketsClick,
      handleShare,
      getImageUrl,
      usersChecking,
      isHeatingUp,
      badgesLoading,
      hideComedianAvatar,
      hideComedianName,
      hideHeatingUp,
      hideUsersChecking,
      hasBadges,
      hasPresaleCode,
      isTixologi,
      venueSlug,
      location,
      isNearby,
      internalReferrer,
      isComediansDrawerOpen,
      pageSection,
      ctaSource,
      waitlistSubscription,
    ]
  );

  return (
    <ShowRowContext.Provider value={contextValue}>
      {children}
    </ShowRowContext.Provider>
  );
}

/** For tests only — bypasses hooks and accepts a pre-computed context value. */
export function TestShowRowProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: ShowRowContextValue;
}) {
  return (
    <ShowRowContext.Provider value={value}>{children}</ShowRowContext.Provider>
  );
}

export function useShowRowContext(): ShowRowContextValue {
  const context = useContext(ShowRowContext);
  if (!context) {
    throw new Error("useShowRowContext must be used within a ShowRowProvider");
  }
  return context;
}
